<script>
import numeral from 'numeral'
import { FileService } from '@/services'
import { useEvidence } from '@/use/evidence'
import { toThaiDatetime } from '@/utils/datetime'

import { NumberInput } from '@/components/form/number-input'
import { FileUpload } from '@/components/page/file-upload'
import {
  FilePreview,
  FilePreviewList,
  FilePreviewListItem
} from '@/components/page/file-preview'
import { BranchProvider, MethodProvider } from '@/providers'
import { mapGetters } from 'vuex'

export default {
  props: {
    data: {
      type: Object,
      default: () => ({})
    },

    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isUploading: false
    }
  },

  computed: {
    ...mapGetters('auth', ['userBranchId'])
  },

  methods: {
    emit(key, value) {
      this.$emit('change', { ...this.data, [key]: value })
    },

    async upload(files) {
      this.isUploading = true

      const promises = files
        .filter(file => {
          if (!/^image\/.+|application\/pdf$/.test(file.type)) {
            this.$message.warning(`${file.name} ไม่สามารถอัปโหลดได้`)
            return false
          }
          return true
        })
        .map(
          async file =>
            await FileService.create(file)
              .then(res => res.data)
              .then(({ status, result }) => {
                if (status === 'error') {
                  throw new Error(`${file.name} มีปัญหาในการอัปโหลด`)
                }
                return result
              })
              .then(({ id, attachment }) => {
                this.emit('evidences', [
                  ...this.data.evidences,
                  useEvidence({ id, url: attachment })
                ])

                this.$message.success(`อัปโหลด ${file.name} เรียบร้อยแล้ว`)
              })
              .catch(err => {
                this.$message.error(err.message)
              })
        )

      await Promise.all(promises)

      this.isUploading = false
    },

    removeEvidence(index) {
      this.emit(
        'evidences',
        this.data.evidences.filter(
          (evidence, evidenceIndex) => evidenceIndex !== index
        )
      )
    }
  },

  render() {
    const Information = ({ slots }) => (
      <el-card>
        {slots().default && (
          <div style={{ marginBottom: '1rem' }}>{slots().default}</div>
        )}
        <el-form labelWidth="6rem">
          <el-row type="flex" gutter={32} style={{ flexWrap: 'wrap' }}>
            <el-col sm={12}>
              <el-form-item label="วันที่โอน:" required={!this.readonly}>
                {this.readonly ? (
                  toThaiDatetime(this.data.datetime, 'D MMMM Y H:mm น.')
                ) : (
                  <el-date-picker
                    value={this.data.datetime}
                    type="datetime"
                    style={{ width: '100%' }}
                    onInput={value => {
                      this.emit('datetime', value)
                    }}
                  />
                )}
              </el-form-item>
            </el-col>
            <el-col sm={12}>
              <el-form-item label="ผู้โอน:">
                {this.data.transferor}
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" gutter={32} style={{ flexWrap: 'wrap' }}>
            <el-col sm={12}>
              <el-form-item label="ต้นทาง:">
                <span style={{ fontWeight: 'bold' }}>{this.data.origin}</span>
              </el-form-item>
            </el-col>
            <el-col sm={12}>
              <el-form-item label="ช่องทาง:" required={!this.readonly}>
                {this.readonly ? (
                  this.data.originMethod && this.data.originMethod.name
                ) : (
                  <MethodProvider
                    scopedSlots={{
                      default: ({ items }) => (
                        <el-select
                          value={this.data.originMethod}
                          valueKey="id"
                          filterable
                          style={{ width: '100%' }}
                          onChange={value => {
                            this.emit('originMethod', value)
                          }}
                          nativeOnTouchend={e => {
                            e.target.readOnly = false
                          }}
                        >
                          {items.map((item, index) => (
                            <el-option
                              key={index}
                              label={item.name}
                              value={item}
                            />
                          ))}
                        </el-select>
                      )
                    }}
                  />
                )}
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" gutter={32} style={{ flexWrap: 'wrap' }}>
            <el-col sm={12}>
              <el-form-item label="ปลายทาง:" required={!this.readonly}>
                {this.readonly ? (
                  this.data.destination && this.data.destination.name
                ) : (
                  <BranchProvider
                    branchId={this.userBranchId}
                    scopedSlots={{
                      default: ({ items }) => (
                        <el-select
                          value={this.data.destination}
                          valueKey="id"
                          filterable
                          style={{ width: '100%' }}
                          onChange={value => {
                            this.emit('destination', value)
                          }}
                          nativeOnTouchend={e => {
                            e.target.readOnly = false
                          }}
                        >
                          {items.map((item, index) => (
                            <el-option
                              key={index}
                              label={item.name}
                              value={item}
                            />
                          ))}
                        </el-select>
                      )
                    }}
                  />
                )}
              </el-form-item>
            </el-col>
            <el-col sm={12}>
              <el-form-item label="ช่องทาง:" required={!this.readonly}>
                {this.readonly ? (
                  this.data.destinationMethod &&
                  this.data.destinationMethod.name
                ) : (
                  <MethodProvider
                    branchId={
                      this.data.destination
                        ? this.data.destination.id
                        : undefined
                    }
                    scopedSlots={{
                      default: ({ items }) => (
                        <el-select
                          value={this.data.destinationMethod}
                          valueKey="id"
                          filterable
                          style={{ width: '100%' }}
                          onChange={value => {
                            this.emit('destinationMethod', value)
                          }}
                          nativeOnTouchend={e => {
                            e.target.readOnly = false
                          }}
                        >
                          {items.map((item, index) => (
                            <el-option
                              key={index}
                              label={item.name}
                              value={item}
                            />
                          ))}
                        </el-select>
                      )
                    }}
                  />
                )}
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" gutter={32} style={{ flexWrap: 'wrap' }}>
            <el-col sm={12}>
              <el-form-item label="จำนวนเงิน:" required={!this.readonly}>
                {this.readonly ? (
                  <span style={{ fontWeight: 'bold' }}>
                    {numeral(this.data.amount).format('0,0.00')} บาท
                  </span>
                ) : (
                  <NumberInput
                    value={this.data.amount}
                    onInput={value => {
                      this.emit('amount', value)
                    }}
                  >
                    <template slot="append">บาท</template>
                  </NumberInput>
                )}
              </el-form-item>
            </el-col>
            <el-col sm={12}>
              <el-form-item label="รหัสอ้างอิง:" required={!this.readonly}>
                {this.readonly ? (
                  this.data.ref
                ) : (
                  <el-input
                    value={this.data.ref}
                    onInput={value => {
                      this.emit('ref', value)
                    }}
                  />
                )}
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>
    )

    const Evidence = () => (
      <el-card style={{ marginTop: '1.5rem' }}>
        <h2>{this.readonly ? 'หลักฐาน/สลิป' : 'แนบหลักฐาน/สลิป'}</h2>
        {!this.readonly && (
          <FileUpload uploading={this.isUploading} onChange={this.upload} />
        )}
        <FilePreviewList style={{ marginTop: '1rem' }}>
          {this.data.evidences.map((item, index) => (
            <FilePreviewListItem key={index}>
              <FilePreview
                src={item.url}
                style={{ width: '150px', height: '150px' }}
                removable={!this.readonly}
                onRemove={() => {
                  this.removeEvidence(index)
                }}
              />
            </FilePreviewListItem>
          ))}
        </FilePreviewList>
      </el-card>
    )

    const Note = () => (
      <el-card style={{ marginTop: '1.5rem' }}>
        <el-form labelPosition="top">
          <el-form-item label="หมายเหตุ">
            {this.readonly ? (
              <div style={{ lineHeight: '1.5' }}>{this.data.note}</div>
            ) : (
              <el-input
                value={this.data.note}
                type="textarea"
                autosize={{ minRows: 3 }}
                onInput={value => {
                  this.emit('note', value)
                }}
              />
            )}
          </el-form-item>
        </el-form>
      </el-card>
    )

    return (
      <div>
        <Information>{this.$slots.header}</Information>
        {!this.readonly || (this.readonly && this.data.evidences.length) ? (
          <Evidence />
        ) : null}
        {!this.readonly || (this.readonly && this.data.note) ? <Note /> : null}
        {this.$slots.footer}
      </div>
    )
  }
}
</script>
